import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SubscriptionPlan } from "@kells/interfaces/patient";
import { BehaviorSubject, combineLatest } from "rxjs";
import {
  BackToPlansClick,
  ShowAddressSection,
} from "apps/patient-reporting/src/app/shared/models/analytics/home-events";
import { AnalyticsService } from "@kells/apis/analytics";
import { UpgradePlanListMode } from "../upgrade-plan-list/upgrade-plan-list.component";
import { map } from "rxjs/operators";

enum ModalMode {
  Address = UpgradePlanListMode.Address,
  Plans = UpgradePlanListMode.Plans,
}

const ModalModeToEventMap = {
  [ModalMode.Address]: ShowAddressSection,
  [ModalMode.Plans]: BackToPlansClick,
};

export interface UpgradePlanModalData {
  title?: string;
  description?: string;
  selectedPlan?: SubscriptionPlan;
  availableUpgrades?: SubscriptionPlan[];
}

@Component({
  selector: "koa-upgrade-plan-modal",
  templateUrl: "./upgrade-plan-modal.component.html",
  styleUrls: ["./upgrade-plan-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradePlanModalComponent {
  title = "";
  description = "";
  selectedPlan: SubscriptionPlan | undefined = undefined;
  availableUpgrades = [
    SubscriptionPlan.Basic,
    SubscriptionPlan.Premium,
    SubscriptionPlan.Complete,
  ];

  public readonly ModalMode = ModalMode;

  public readonly title$ = new BehaviorSubject<string>("");

  public readonly mode$ = new BehaviorSubject<ModalMode>(ModalMode.Plans);

  public readonly modalTitle$ = combineLatest([this.title$, this.mode$]).pipe(
    map(([title, mode]) =>
      mode === ModalMode.Address
        ? "Manage Your Plan | KELLS Complete Plan"
        : title
    )
  );

  constructor(
    public dialogRef: MatDialogRef<UpgradePlanModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpgradePlanModalData,
    private readonly analyticsService: AnalyticsService
  ) {
    this.title$.next(this.data?.title ?? "Manage Your Plan");
    this.description =
      this.data?.description ??
      "Make the most out of your KELLS account by managing your plan!";
    this.availableUpgrades = this.data?.availableUpgrades ?? [
      SubscriptionPlan.Basic,
      SubscriptionPlan.Premium,
      SubscriptionPlan.Complete,
    ];
    this.selectedPlan = this.data?.selectedPlan ?? undefined;
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public setMode(mode: ModalMode) {
    this.mode$.next(mode);
    this.analyticsService.record(ModalModeToEventMap[mode]({}));
  }
}
